/**
 * The date range's arithmetic, apart from the panel that draws it.
 *
 * **Every date here is handled in UTC and as a `YYYY-MM-DD` string.** That is
 * not fussiness. `new Date("2026-08-02")` parses as midnight UTC while
 * `new Date(2026, 7, 2)` is midnight *local*, and mixing them shifts a range by
 * a day for anyone west of Greenwich — the kind of bug that only appears for
 * some users, in some months, and is blamed on the data. The one conversion
 * point is `toKey`/`fromKey` below, and nothing else touches a Date's local
 * getters.
 *
 * A range is stored as two keys rather than a Date pair for the same reason:
 * the URL, the API and the display all want the string, so the string is the
 * value and the Date is only ever a temporary for arithmetic.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export interface DayRange {
  readonly from: string;
  readonly to: string;
}

export interface RangePreset extends DayRange {
  readonly label: string;
}

/** UTC midnight for a `YYYY-MM-DD` key. */
export function fromKey(key: string): Date {
  return new Date(`${key}T00:00:00.000Z`);
}

/** `YYYY-MM-DD` for a Date, read in UTC. */
export function toKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(key: string, days: number): string {
  return toKey(new Date(fromKey(key).getTime() + days * DAY_MS));
}

/** First day of the month a key falls in. */
export function monthStart(key: string): Date {
  const date = fromKey(key);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

export function shiftMonths(month: Date, by: number): Date {
  return new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + by, 1));
}

/**
 * The presets, as a function of today rather than a fixed table.
 *
 * "Last 14 days" means 14 days including today, so the span is 13 days back —
 * off-by-one here is the difference between a fortnight and a fortnight and a
 * day, and it shows up as a chart that disagrees with the label above it.
 */
export function presets(today: string): readonly RangePreset[] {
  const start = monthStart(today);
  const lastMonth = shiftMonths(start, -1);
  return [
    { label: "Month to date", from: toKey(start), to: today },
    {
      label: "Last month",
      from: toKey(lastMonth),
      to: addDays(toKey(start), -1),
    },
    { label: "Last 14 days", from: addDays(today, -13), to: today },
    { label: "Last 30 days", from: addDays(today, -29), to: today },
    { label: "Last 60 days", from: addDays(today, -59), to: today },
    { label: "Last 90 days", from: addDays(today, -89), to: today },
  ];
}

export const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;

export const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** Every cell of a month grid, including the blanks that pad the first week. */
export function monthCells(month: Date): readonly (string | null)[] {
  const firstWeekday = month.getUTCDay();
  const days = new Date(
    Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const cells: (string | null)[] = Array.from({ length: firstWeekday }, () => null);
  for (let day = 1; day <= days; day++) {
    cells.push(
      toKey(new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), day))),
    );
  }
  return cells;
}

/** The preset a range is, by name, when it is one. */
export function presetName(from: string, to: string, today: string): string | null {
  const match = presets(today).find((preset) => preset.from === from && preset.to === to);
  return match?.label ?? null;
}

/**
 * The days a range covers, as a person says them: "Sep 14 – Oct 12", with
 * the year on an end that is not in today's year ("Dec 20, 2025 – Jan 4").
 */
export function spanLabel(from: string, to: string, today: string): string {
  const year = fromKey(today).getUTCFullYear();
  const day = (key: string) => {
    const date = fromKey(key);
    const words = `${MONTH_NAMES[date.getUTCMonth()] ?? ""} ${String(date.getUTCDate())}`;
    return date.getUTCFullYear() === year
      ? words
      : `${words}, ${String(date.getUTCFullYear())}`;
  };
  return from === to ? day(from) : `${day(from)} – ${day(to)}`;
}

/**
 * A day's state: an end of the range, inside it, or the anchor of one being
 * drawn. Kept out of the component because it is the fiddly part and reads
 * better named.
 */
export function cellClass(
  key: string,
  {
    from,
    to,
    anchor,
    today,
  }: {
    readonly from: string;
    readonly to: string;
    readonly anchor: string | null;
    readonly today: string;
  },
): string {
  const classes = ["daterange-day"];
  if (key > today) classes.push("daterange-day-future");
  if (anchor !== null) {
    if (key === anchor) classes.push("daterange-day-end");
  } else {
    if (key === from || key === to) classes.push("daterange-day-end");
    else if (key > from && key < to) classes.push("daterange-day-in");
  }
  return classes.join(" ");
}
